import React, { useState, useEffect } from "react";
import { Footer } from "../components/Footer.js";
import { Slide_title } from "./../components/slide-title.js";
import Validation from "../components/validation.js";
import ErrorSnackbar from "../components/ErrorSnackbar.js";
import SuccessSnackbar from "../components/SuccessSnackbar.js";
import { AuthRegistration, portal_listing } from "../service/api.js";
import { useNavigate } from "react-router-dom";
import Head_CateHead from "../components/Head_CateHead.js";
import { AddressForm, validateAddress, mirrorBillingToShipping } from "../components/AddressForm.js";

export const Signup = () => {
    const navigate = useNavigate();
    const [ExceptionError, setExceptionError] = useState([]);
    const [successMessages, setSuccessMessages] = useState([]);
    const [showBillingInfo, setShowBillingInfo] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ── Step 1: Personal info ──────────────────────────────────────────────
    const [values, setValues] = useState({
        Firstname: "",
        Lastname: "",
        MobileNumber: "",
        Email: "",
        CustomerType: "1",  // default to Retailer
        Password: "",
        BusinessName: "",
        IndustryName: "",
        Webaddress: "",
    });

    // ── Step 2: Billing + Shipping address ────────────────────────────────
    // Fields use the AddressForm prefix convention:
    //   billing_Company, billing_Building, billing_StreetAddress,
    //   billing_Suburb, billing_Postcode, billing_State
    //   (same for shipping_*)
    const [addressValues, setAddressValues] = useState({
        billing_Company: "",
        billing_Building: "",
        billing_StreetAddress: "",
        billing_Suburb: "",
        billing_Postcode: "",
        billing_State: "",
        shipping_Company: "",
        shipping_Building: "",
        shipping_StreetAddress: "",
        shipping_Suburb: "",
        shipping_Postcode: "",
        shipping_State: "",
    });

    // Whether the user wants a separate shipping address
    const [shippingDifferent, setShippingDifferent] = useState(false);

    const [errors, setErrors] = useState({});
    const [portallist, setPortallist] = useState([]);

    useEffect(() => {
        const fetchPortalList = async () => {
            try {
                const response = await portal_listing();
                const extractedPortalList = extractPortalList(response.data);
                setPortallist(extractedPortalList);
            } catch (error) {
                console.error("Error fetching portal list", error);
            }
        };
        fetchPortalList();
    }, []);

    const extractPortalList = (data) => {
        const portalArray = data.Data;
        return portalArray.map((portal) => ({
            id: portal.PortalID,
            title: portal.PortalName || "Default Title",
        }));
    };

    // ── Handlers ──────────────────────────────────────────────────────────

    function handleInput(event) {
        const { name, value } = event.target;
        setValues((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Called by AddressForm components via onChange(fieldName, value)
    const handleAddressChange = (fieldName, value) => {
        setAddressValues((prev) => ({ ...prev, [fieldName]: value }));
        setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
    };

    function handleCheckboxChange(event) {
        setShippingDifferent(event.target.checked);
        // Clear any shipping errors when toggled off
        if (!event.target.checked) {
            setErrors((prev) => {
                const next = { ...prev };
                Object.keys(next).forEach((k) => {
                    if (k.startsWith("shipping_")) delete next[k];
                });
                return next;
            });
        }
    }

    // ── Validation ────────────────────────────────────────────────────────

    function validateStep1() {
        const errs = Validation(values);
        if (!values.BusinessName?.trim()) {
            errs.BusinessName = "Business Name is required.";
        }
        return errs;
    }

    function validateStep2() {
        const errs = validateAddress(addressValues, "billing");
        if (shippingDifferent) {
            Object.assign(errs, validateAddress(addressValues, "shipping"));
        }
        return errs;
    }

    // ── Form submission ───────────────────────────────────────────────────

    function handleValidation(event) {
        event.preventDefault();

        if (!showBillingInfo) {
            // Validate personal info step
            const validationErrors = validateStep1();
            setErrors(validationErrors);
            if (Object.keys(validationErrors).length === 0) {
                setShowBillingInfo(true);
            }
        } else {
            // Validate address step
            const validationErrors = validateStep2();
            setErrors(validationErrors);
            if (Object.keys(validationErrors).length === 0) {
                doRegister();
            }
        }
    }

    async function doRegister() {
        setIsSubmitting(true);

        // Build the payload, mirroring billing → shipping when not different
        const resolved = shippingDifferent ? addressValues : mirrorBillingToShipping(addressValues);

        const payload = {
            // Personal
            FullName: `${values.Firstname} ${values.Lastname}`,
            Customername: `${values.Firstname} ${values.Lastname}`,
            MobileNumber: values.MobileNumber,
            Email: values.Email,
            UserName: values.Email,
            RoleID: values.CustomerType,
            Password: values.Password,
            BusinessName: values.BusinessName,
            IndustryName: values.IndustryName,
            WebAddress: values.Webaddress,
            // Billing
            BillingCompany: resolved.billing_Company,
            BillingBuilding: resolved.billing_Building,
            Address1: resolved.billing_StreetAddress +' '+resolved.billing_Suburb,
            BZipcode: resolved.billing_Postcode,
            State: resolved.billing_State,
            City: resolved.billing_Suburb,
            Country : 'AUS',
            // Shipping
            ShippingCustomername: `${values.Firstname} ${values.Lastname}`,
            ShippingCompany: resolved.shipping_Company,
            ShippingAddress1: resolved.shipping_Building+' '+resolved.shipping_StreetAddress,
            ShippingCity: resolved.shipping_Suburb,
            ShippingZipcode: resolved.shipping_Postcode,
            ShippingState: resolved.shipping_State,
            ShippingCountry: 'AUS',
            
        };

        try {
            const res = await AuthRegistration(payload);
            if (res.status === 200) {
                handleExceptionSuccessMessages(res);
                setTimeout(() => navigate("/signin"), 2500);
            } else {
                handleExceptionError(
                    res?.response?.data?.ErrorMessage || res?.data?.ErrorMessage || "An error occurred"
                );
            }
        } catch (error) {
            handleExceptionError(
                error.response?.data?.ErrorMessage || "An unexpected error occurred"
            );
        } finally {
            setIsSubmitting(false);
        }
    }

    // ── Error / Success helpers ───────────────────────────────────────────

    function handleExceptionError(msg) {
        setExceptionError((prev) => [...prev, { id: Date.now(), message: msg }]);
    }

    function handleExceptionSuccessMessages(res) {
        setSuccessMessages((prev) => [
            ...prev,
            { id: Date.now(), message: res.data.Message },
        ]);
    }

    function clearErrors(id) {
        setExceptionError((prev) => prev.filter((m) => m.id !== id));
    }

    function clearSuccess(id) {
        setSuccessMessages((prev) => prev.filter((m) => m.id !== id));
    }

    // ── Render ────────────────────────────────────────────────────────────

    return (
        <div>
            <ErrorSnackbar errorMessages={ExceptionError} onClearErrors={clearErrors} />
            <SuccessSnackbar successMessages={successMessages} onclearSuccess={clearSuccess} />
            <Head_CateHead />
            <div className="container sign_container">
                <Slide_title
                    title={showBillingInfo ? "Signup — Address Information" : "Signup"}
                    className="signup_page_title"
                />
                <div className="sign_up_wrapper">
                    <form onSubmit={handleValidation} noValidate>

                        {/* ════════════════════════════════════════════════
                            STEP 1 — Personal Information
                        ════════════════════════════════════════════════ */}
                        {!showBillingInfo && (
                            <>
                                <div className="checkout_form_tel">
                                    <h3 className="required">First Name</h3>
                                    <input
                                        type="text"
                                        placeholder="First Name"
                                        name="Firstname"
                                        value={values.Firstname}
                                        onChange={handleInput}
                                    />
                                    {errors.Firstname && <p className="giv_error">{errors.Firstname}</p>}
                                </div>

                                <div className="checkout_form_tel">
                                    <h3 className="required">Last Name</h3>
                                    <input
                                        type="text"
                                        placeholder="Last Name"
                                        name="Lastname"
                                        value={values.Lastname}
                                        onChange={handleInput}
                                    />
                                    {errors.Lastname && <p className="giv_error">{errors.Lastname}</p>}
                                </div>

                                <div className="checkout_form_tel">
                                    <h3 className="required">Email</h3>
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        name="Email"
                                        value={values.Email}
                                        onChange={handleInput}
                                    />
                                    {errors.emailerr && <p className="giv_error">{errors.emailerr}</p>}
                                </div>

                                <div className="checkout_form_tel">
                                    <h3 className="required">Mobile Number</h3>
                                    <input
                                        type="tel"
                                        placeholder="Mobile Number"
                                        name="MobileNumber"
                                        value={values.MobileNumber}
                                        onChange={handleInput}
                                    />
                                    {errors.MobileNumber && <p className="giv_error">{errors.MobileNumber}</p>}
                                </div>

                                <div className="checkout_form_tel">
                                    <h3 className="required">Customer Type</h3>
                                    <select name="CustomerType" value={values.CustomerType} onChange={handleInput}>
                                        <option value="1">Retailer</option>
                                        <option value="2">Wholesaler</option>
                                    </select>
                                    {errors.CustomerType && <p className="giv_error">{errors.CustomerType}</p>}
                                </div>

                                <div className="checkout_form_tel">
                                    <h3 className="required">Business Name <small style={{ fontWeight: 400, fontSize: "0.8em" }}>(enter full name if private sale)</small></h3>
                                    <input
                                        type="text"
                                        placeholder="Business Name"
                                        name="BusinessName"
                                        value={values.BusinessName}
                                        onChange={handleInput}
                                    />
                                    {errors.BusinessName && <p className="giv_error">{errors.BusinessName}</p>}
                                </div>

                                <div className="checkout_form_tel">
                                    <h3>Industry Name <span className="field-optional">(Optional)</span></h3>
                                    <input
                                        type="text"
                                        placeholder="Industry Name"
                                        name="IndustryName"
                                        value={values.IndustryName}
                                        onChange={handleInput}
                                    />
                                </div>

                                <div className="checkout_form_tel">
                                    <h3>Web Address <span className="field-optional">(Optional)</span></h3>
                                    <input
                                        type="text"
                                        placeholder="https://yourwebsite.com"
                                        name="Webaddress"
                                        value={values.Webaddress}
                                        onChange={handleInput}
                                    />
                                    {errors.Webaddress && <p className="giv_error">{errors.Webaddress}</p>}
                                </div>

                                <div className="checkout_form_tel">
                                    <h3 className="required">Password</h3>
                                    <input
                                        type="password"
                                        placeholder="Password"
                                        name="Password"
                                        onChange={handleInput}
                                    />
                                    {errors.Password && <p className="giv_error">{errors.Password}</p>}
                                </div>

                                <button className="frm_button form_submit_form" type="submit">
                                    Next
                                </button>
                            </>
                        )}

                        {/* ════════════════════════════════════════════════
                            STEP 2 — Billing & Shipping Address
                        ════════════════════════════════════════════════ */}
                        {showBillingInfo && (
                            <>
                                {/* ── Billing Address ──────────────────── */}
                                <AddressForm
                                    prefix="billing"
                                    title="Billing Address"
                                    values={addressValues}
                                    errors={errors}
                                    onChange={handleAddressChange}
                                />

                                {/* ── Shipping checkbox ─────────────────── */}
                                <div className="address-shipping-toggle">
                                    <label className="address-shipping-toggle__label">
                                        <input
                                            type="checkbox"
                                            checked={shippingDifferent}
                                            onChange={handleCheckboxChange}
                                            className="address-shipping-toggle__checkbox"
                                        />
                                        <span>MY SHIPPING ADDRESS IS DIFFERENT TO MY BILLING ADDRESS</span>
                                    </label>
                                </div>

                                {/* ── Shipping Address (conditional) ────── */}
                                {shippingDifferent && (
                                    <AddressForm
                                        prefix="shipping"
                                        title="Shipping Address"
                                        values={addressValues}
                                        errors={errors}
                                        onChange={handleAddressChange}
                                    />
                                )}

                                {/* ── Navigation buttons ────────────────── */}
                                <div className="signup-btn-row">
                                    <button
                                        type="button"
                                        className="frm_button form_submit_form"
                                        onClick={() => setShowBillingInfo(false)}
                                        style={{ marginRight: "10px" }}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className="frm_button form_submit_form"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "Signing Up…" : "Sign Up"}
                                    </button>
                                </div>
                            </>
                        )}

                    </form>
                </div>
            </div>
            <Footer />
        </div>
    );
};
